<template>
  <div
    class="question-item likert-composite-wjx"
    :class="{ 'has-error': hasError }"
  >
    <!-- 题目编号 + 主标题（条件性 sticky） -->
    <div
      ref="questionHeaderRef"
      class="likert-question-header"
      :class="{ 'sticky-header': shouldEnableSticky }"
    >
      <span class="required-mark" v-if="required">*</span
      ><span class="question-index">{{ questionNumber }}.</span
      ><span class="question-title">{{ title }}</span
      ><span v-if="hasError" class="error-hint">（请填写此项）</span>
    </div>

    <!-- 子题列表（纵向排列，按子题顺序平铺，在分组边界前插入列头） -->
    <div class="likert-subquestions">
      <template v-for="(subQ, subIdx) in subQuestions" :key="subQ.id">
        <!-- 分组列头（在分组边界前插入，sticky） -->
        <div
          v-if="shouldShowHeaderBefore(subIdx)"
          class="likert-header-row"
          :class="{ 'sticky-header-row': shouldEnableSticky }"
          :style="stickyHeaderStyle"
        >
          <div
            v-for="(option, idx) in getCurrentGroupHeaderOptions(subIdx)"
            :key="idx"
            class="likert-header-cell"
          >
            <span class="likert-header-text">{{ option }}</span>
          </div>
        </div>

        <!-- 子题项（每个子题 = 一个独立的单选题，只显示当前分组的选项） -->
        <div class="likert-subquestion-item">
          <!-- 子题文本（在上） -->
          <div class="likert-subquestion-label">
            {{ subQ.title }}
          </div>

          <!-- 选项行（在下，只显示当前分组的选项） -->
          <div class="likert-radio-group">
            <label
              v-for="(option, localIdx) in getCurrentGroupOptions(subIdx)"
              :key="localIdx"
              class="likert-radio-option"
              :class="{ selected: isOptionSelected(subQ.id, subIdx, localIdx) }"
              @click="handleOptionChange(subQ.id, subIdx, localIdx, $event)"
            >
              <input
                type="radio"
                :name="`likert-${questionId}-${subQ.id}`"
                :value="getGlobalOptionIndex(subIdx, localIdx)"
                :checked="isOptionSelected(subQ.id, subIdx, localIdx)"
                @change="handleOptionChange(subQ.id, subIdx, localIdx, $event)"
                class="likert-radio-input"
              />
              <span class="likert-radio-indicator"></span>
            </label>
          </div>
          <!-- 选择提示 tooltip -->
          <div
            v-if="
              likertTooltip.visible && likertTooltip.subQuestionId === subQ.id
            "
            class="likert-tooltip"
            :style="{
              left: likertTooltip.left + 'px',
              top: likertTooltip.top + 'px',
            }"
          >
            {{ likertTooltip.text }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "LikertCompositeQuestion",
  props: {
    hasError: {
      type: Boolean,
      default: false,
    },
    questionId: {
      type: String,
      required: true,
    },
    displayIndex: {
      type: Number,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    required: {
      type: Boolean,
      default: true,
    },
    likertConfig: {
      type: Object,
      required: true,
      validator: (value) => {
        return (
          value.leftLabel && value.rightLabel && Array.isArray(value.options)
        );
      },
    },
    subQuestions: {
      type: Array,
      required: true,
      validator: (value) => {
        // 允许至少 1 个子题（如第 40 题消极感受只有 1 个子题）
        return value.length >= 1 && value.every((q) => q.id && q.title);
      },
    },
    modelValue: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      questionHeaderHeight: 60, // 默认高度，将通过 ref 动态更新
      likertTooltip: {
        visible: false,
        text: "",
        subQuestionId: null,
        left: 0,
        top: 0,
      },
      likertTooltipTimer: null, // tooltip 自动隐藏的定时器
    };
  },
  computed: {
    // 从题目ID提取序号（如 "q49" -> 49）
    questionNumber() {
      if (this.questionId) {
        const match = this.questionId.match(/q(\d+)/);
        if (match && match[1]) {
          return parseInt(match[1], 10);
        }
      }
      // 降级方案：如果无法从ID提取，使用传入的displayIndex
      return this.displayIndex || 0;
    },
    // 获取当前复合题的答案对象（从父组件的 answers[questionId] 中获取）
    compositeAnswers() {
      return this.modelValue[this.questionId] || {};
    },

    // 选项总数
    totalOptions() {
      return this.likertConfig.options ? this.likertConfig.options.length : 0;
    },

    // 是否需要分组
    // 规则：选项数 ≤ 6：不分组；选项数 = 7：必须分组；选项数 > 7：每6个一组
    needsGrouping() {
      return this.totalOptions > 6;
    },

    // 子题数量
    subQuestionCount() {
      return this.subQuestions ? this.subQuestions.length : 0;
    },

    // 是否需要启用 sticky 标题
    shouldEnableSticky() {
      if (this.needsGrouping) {
        return true;
      }
      return this.subQuestionCount >= 10;
    },

    // 分组配置（按子题分组，不是按选项分组）
    // 7个选项时，所有子题都显示全部7个选项，但按子题数量分组显示（避免屏幕宽度问题）
    optionGroups() {
      // 不需要分组：所有子题显示全部选项
      if (!this.needsGrouping) {
        return [
          {
            start: 0,
            end: this.totalOptions,
            options: this.likertConfig.options,
          },
        ];
      }

      // 7个选项：所有子题都显示全部7个选项
      // 分组只是为了在滚动时重复显示列头，不是限制选项
      if (this.totalOptions === 7) {
        return [
          {
            start: 0,
            end: 7,
            options: this.likertConfig.options, // 全部7个选项
          },
        ];
      }

      // 超过7个选项：每6个选项一组（这种情况才需要按选项分组）
      const groups = [];
      for (let i = 0; i < this.totalOptions; i += 6) {
        const end = Math.min(i + 6, this.totalOptions);
        groups.push({
          start: i,
          end: end,
          options: this.likertConfig.options.slice(i, end),
        });
      }
      return groups;
    },

    // 每个子题属于哪个分组（按滚动段切分，用于在分组边界前插入列头）
    // 规则：按子题顺序平铺，在分组边界前插入列头，所有子题都显示全部选项
    subQuestionGroupIndex() {
      const map = {};
      if (!this.needsGrouping) {
        this.subQuestions.forEach((subQ) => {
          map[subQ.id] = 0;
        });
        return map;
      }

      // 需要分组：按子题数量分配到各组（用于列头显示，不影响选项显示）
      if (this.totalOptions === 7) {
        // 7个选项：所有子题都显示全部7个选项
        // 但按子题数量分组，在分组边界前重复显示列头
        const midPoint = Math.floor(this.subQuestions.length / 2);
        this.subQuestions.forEach((subQ, idx) => {
          map[subQ.id] = idx < midPoint ? 0 : 1;
        });
        return map;
      }

      // 超过7个选项：按子题数量平均分配到各组
      const groupSize = Math.ceil(
        this.subQuestions.length / this.optionGroups.length
      );
      this.subQuestions.forEach((subQ, idx) => {
        const groupIdx = Math.floor(idx / groupSize);
        map[subQ.id] = Math.min(groupIdx, this.optionGroups.length - 1);
      });
      return map;
    },

    // Sticky 列头的样式（动态计算 top 值）
    stickyHeaderStyle() {
      if (!this.shouldEnableSticky) {
        return {};
      }
      return {
        top: `${this.questionHeaderHeight}px`,
      };
    },
  },
  mounted() {
    this.updateQuestionHeaderHeight();
    // 监听窗口大小变化，重新计算高度
    window.addEventListener("resize", this.updateQuestionHeaderHeight);
  },
  beforeUnmount() {
    // 清理事件监听
    window.removeEventListener("resize", this.updateQuestionHeaderHeight);
    // 清理 tooltip 定时器
    if (this.likertTooltipTimer) {
      clearTimeout(this.likertTooltipTimer);
      this.likertTooltipTimer = null;
    }
  },
  watch: {
    // 当 sticky 状态变化时，重新计算高度
    shouldEnableSticky: {
      immediate: false,
      handler() {
        this.$nextTick(() => {
          this.updateQuestionHeaderHeight();
        });
      },
    },
  },
  methods: {
    // 更新题目标题高度（用于计算列头 sticky top）
    updateQuestionHeaderHeight() {
      this.$nextTick(() => {
        if (this.$refs.questionHeaderRef) {
          this.questionHeaderHeight = this.$refs.questionHeaderRef.offsetHeight;
        }
      });
    },

    // 判断是否应该在当前子题前显示列头
    // 规则：在分组边界前插入新的列头
    shouldShowHeaderBefore(subQuestionIndex) {
      if (!this.needsGrouping) {
        // 不需要分组：只在第一个子题前显示
        return subQuestionIndex === 0;
      }

      // 需要分组：在第一个子题前显示第一组列头
      if (subQuestionIndex === 0) {
        return true;
      }

      // 在分组边界前插入列头（排除第一个子题）
      let groupSize;
      if (this.totalOptions === 7) {
        // 7个选项：使用中点作为分组边界
        groupSize = Math.floor(this.subQuestions.length / 2);
      } else {
        // 超过7个选项：按组数平均分配
        groupSize = Math.ceil(
          this.subQuestions.length / this.optionGroups.length
        );
      }
      return subQuestionIndex > 0 && subQuestionIndex % groupSize === 0;
    },

    // 获取当前子题应该显示的列头选项
    getCurrentGroupHeaderOptions(subQuestionIndex) {
      // 7个选项时，所有子题都显示全部7个选项
      if (this.totalOptions === 7) {
        return this.likertConfig.options; // 返回全部7个选项
      }

      if (!this.needsGrouping) {
        return this.likertConfig.options;
      }

      // 超过7个选项：获取当前子题所属的分组
      const subQ = this.subQuestions[subQuestionIndex];
      const groupIndex = this.subQuestionGroupIndex[subQ.id];
      return this.optionGroups[groupIndex]?.options || [];
    },

    // 获取当前子题应该显示的选项组
    getCurrentGroupOptions(subQuestionIndex) {
      // 7个选项时，所有子题都显示全部7个选项
      if (this.totalOptions === 7) {
        return this.likertConfig.options; // 返回全部7个选项
      }

      if (!this.needsGrouping) {
        // 不需要分组：显示所有选项
        return this.likertConfig.options;
      }

      // 超过7个选项：只显示当前分组的选项
      const subQ = this.subQuestions[subQuestionIndex];
      const groupIndex = this.subQuestionGroupIndex[subQ.id];
      return this.optionGroups[groupIndex]?.options || [];
    },

    // 获取全局选项索引（从本地索引转换为全局索引）
    getGlobalOptionIndex(subQuestionIndex, localIndex) {
      // 7个选项时，所有子题都显示全部7个选项，localIndex 就是全局索引
      if (this.totalOptions === 7) {
        return localIndex;
      }

      if (!this.needsGrouping) {
        return localIndex;
      }

      // 超过7个选项：根据分组计算全局索引
      const subQ = this.subQuestions[subQuestionIndex];
      const groupIndex = this.subQuestionGroupIndex[subQ.id];
      const group = this.optionGroups[groupIndex];
      return group.start + localIndex;
    },

    // 判断选项是否被选中
    isOptionSelected(subQuestionId, subQuestionIndex, localIndex) {
      const globalIndex = this.getGlobalOptionIndex(
        subQuestionIndex,
        localIndex
      );
      return this.compositeAnswers[subQuestionId] === globalIndex;
    },

    // 处理选项变化（每个子题是独立的单选题）
    handleOptionChange(subQuestionId, subQuestionIndex, localIndex, event) {
      // 获取全局选项索引
      const globalIndex = this.getGlobalOptionIndex(
        subQuestionIndex,
        localIndex
      );

      // 更新复合题的答案对象（只更新当前子题的答案）
      const compositeAnswers = {
        ...this.compositeAnswers,
        [subQuestionId]: globalIndex,
      };

      // 更新整个 answers 对象
      const newValue = {
        ...this.modelValue,
        [this.questionId]: compositeAnswers,
      };

      this.$emit("update:modelValue", newValue);

      // 显示提示 tooltip
      this.showLikertTooltip(
        subQuestionId,
        subQuestionIndex,
        localIndex,
        event
      );
    },
    /**
     * 显示 Likert 复合题选择提示
     * @param {string} subQuestionId - 子题 ID
     * @param {number} subQuestionIndex - 子题索引
     * @param {number} localIndex - 选项在分组中的本地索引
     * @param {Event} event - 点击事件对象
     */
    showLikertTooltip(subQuestionId, subQuestionIndex, localIndex, event) {
      // 清除之前的定时器，避免重复显示
      if (this.likertTooltipTimer) {
        clearTimeout(this.likertTooltipTimer);
        this.likertTooltipTimer = null;
      }

      // 获取选项文本
      const options = this.getCurrentGroupOptions(subQuestionIndex);
      const optionText = options[localIndex] || "";

      if (!optionText) {
        return; // 无法获取选项文本，静默失败
      }

      // 获取点击的目标元素
      const target =
        event?.currentTarget || event?.target?.closest(".likert-radio-option");
      if (!target) {
        return; // 无法找到目标元素，静默失败
      }

      const rect = target.getBoundingClientRect();
      // 获取容器元素（用于相对定位）
      const container = target.closest(".likert-subquestion-item");
      if (!container) {
        return; // 无法找到容器元素，静默失败
      }

      const containerRect = container.getBoundingClientRect();

      // 计算 tooltip 位置：相对于容器，在选项上方居中显示
      const left = rect.left - containerRect.left + rect.width / 2;
      const top = rect.top - containerRect.top - 10; // 在选项上方 10px

      // 更新 tooltip 状态
      this.likertTooltip = {
        visible: true,
        text: optionText,
        subQuestionId: subQuestionId,
        left: left,
        top: top,
      };

      // 2 秒后自动隐藏
      this.likertTooltipTimer = setTimeout(() => {
        if (this.likertTooltip) {
          this.likertTooltip.visible = false;
        }
        this.likertTooltipTimer = null;
      }, 2000);
    },
  },
};
</script>

<style lang="less" scoped>
.likert-composite-wjx {
  transition: border-color 0.3s, box-shadow 0.3s, background-color 0.3s;

  &.has-error {
    border-left: 4px solid #ff4d4f;
    background-color: #fff1f0;
    box-shadow: 0 2px 8px rgba(255, 77, 79, 0.15);

    .likert-question-header {
      .error-hint {
        color: #ff4d4f;
        font-size: 14px;
        font-weight: normal;
        margin-left: 8px;
        display: inline !important;
      }
    }
  }
  font-size: 16px;
  padding: 16px;
  padding-bottom: 20px;
  line-height: 1.6;
  color: #333;
  background-color: #fff;
  margin-bottom: 12px;
  border-radius: 0;
  // 确保内容不重叠
  overflow: visible;

  .likert-question-header {
    margin-bottom: 28px; // 进一步增加底部间距，确保与列头不重叠
    padding-bottom: 8px; // 增加内部底部padding
    display: block;

    > span {
      display: inline;
    }

    .question-index {
      font-weight: 600;
      margin-right: 2px;
      display: inline !important;
    }

    .question-title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
      display: inline !important;
      margin: 0; // 清除默认margin
      word-wrap: break-word;
      word-break: break-word;
    }

    .required-mark {
      color: #ff0000;
      margin-right: 2px;
      font-weight: normal;
      display: inline !important;
    }

    // Sticky 标题（仅在需要时启用）
    // 固定区域仅包含：必填标记、displayIndex、父题标题文本
    &.sticky-header {
      position: sticky;
      top: 0;
      z-index: 20;
      background-color: #fff;
      padding: 16px;
      padding-bottom: 24px; // 增加底部padding，确保有足够空间
      margin: 0 0 28px 0; // 进一步增加底部margin，确保与列头不重叠
      border-bottom: 1px solid #f0f0f0;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      // 确保在滚动时始终可见，作为语义锚点
      transition: box-shadow 0.2s;
    }
  }

  // 子题列表（纵向排列，每个子题是独立的单选题）
  .likert-subquestions {
    padding: 0;
    margin-top: 0; // 移除顶部margin，由列头自己控制间距

    // 列头行（仅作为标签提示，不参与交互，条件性 sticky）
    .likert-header-row {
      display: flex;
      align-items: flex-start; // 改为顶部对齐，允许文字换行
      justify-content: space-between;
      padding: 12px 8px; // 增加上下padding，减少左右padding
      background-color: #f8f8f8;
      margin-bottom: 12px; // 增加底部间距
      margin-top: 0; // 移除顶部margin，由题目标题的margin-bottom提供间距
      border-radius: 2px;
      // 确保列头不会与题目标题重叠
      position: relative;
      z-index: 1;
      // 弱化边框：只保留非常淡的分隔
      border: none;

      // Sticky 列头（top 值通过 :style 动态设置）
      &.sticky-header-row {
        position: sticky;
        // top 值通过 :style 动态设置为 questionHeaderHeight
        z-index: 10;
        background-color: #f8f8f8;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
      }

      .likert-header-cell {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        min-width: 0; // 允许文字换行
        padding: 0 4px; // 增加左右padding，避免文字过于紧密

        .likert-header-text {
          font-size: 13px; // 稍微减小字体，确保7个选项能显示
          color: #666; // 稍微加深颜色，提升可读性
          line-height: 1.5; // 增加行高，允许文字换行
          word-break: break-word; // 使用break-word而不是break-all，更自然的换行
          white-space: normal;
          text-align: center;
          width: 100%; // 确保文字占满单元格
        }
      }
    }

    // 子题项（每个子题 = 一个独立的单选题）
    .likert-subquestion-item {
      padding: 16px 12px;
      margin-bottom: 16px;
      // 弱化边框：移除所有边框
      border: none;
      background-color: transparent;
      position: relative;

      &:last-child {
        margin-bottom: 0;
      }

      // 子题文本（在上）
      .likert-subquestion-label {
        display: block;
        margin-bottom: 14px; // 增加底部间距，避免与选项重叠
        text-align: left;
        font-size: 16px;
        color: #333;
        line-height: 1.6;
        font-weight: 500;
      }

      // 选项组（在下，横向均匀分布，Radio为主交互）
      // 每个子题只显示当前分组的选项
      .likert-radio-group {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 6px;
        padding: 0;
        // 无边框，无背景，突出Radio
        background-color: transparent;
        border: none;
        // 禁止横向滚动
        overflow-x: hidden;
        width: 100%;
        max-width: 100%;

        .likert-radio-option {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 10px 4px;
          min-height: 44px;
          min-width: 0; // 允许收缩，禁止横向滚动
          position: relative;
          // 弱化容器样式，突出Radio
          background-color: transparent;
          border: none;
          border-radius: 4px;
          transition: background-color 0.2s;

          &:hover {
            background-color: #f5f5f5;
          }

          &.selected {
            background-color: #f0f8ff;
          }

          .likert-radio-input {
            position: absolute;
            opacity: 0;
            width: 0;
            height: 0;
            margin: 0;
            pointer-events: none;
          }

          .likert-radio-indicator {
            width: 22px;
            height: 22px;
            border: 2px solid #d0d0d0;
            border-radius: 50%;
            position: relative;
            transition: all 0.2s;
            flex-shrink: 0;
            background-color: #fff;
            // Radio是主视觉元素，样式突出

            &::after {
              content: "";
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) scale(0);
              width: 10px;
              height: 10px;
              border-radius: 50%;
              background-color: #0095ff;
              transition: transform 0.2s;
              opacity: 0;
            }
          }

          .likert-radio-input:checked + .likert-radio-indicator {
            border-color: #0095ff;
            background-color: #0095ff;
            // 选中状态明显

            &::after {
              transform: translate(-50%, -50%) scale(1);
              opacity: 1;
              background-color: #fff;
            }
          }

          &:hover .likert-radio-indicator {
            border-color: #0095ff;
          }

          .likert-radio-input:focus + .likert-radio-indicator {
            outline: 2px solid rgba(0, 149, 255, 0.3);
            outline-offset: 2px;
          }
        }
      }

      // 选择提示 tooltip
      .likert-tooltip {
        position: absolute;
        background-color: #0095ff;
        color: #fff;
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 14px;
        white-space: nowrap;
        z-index: 1000;
        pointer-events: none;
        transform: translateX(-50%);
        animation: tooltipFadeIn 0.2s ease-out;

        // 小三角箭头（指向下方）
        &::after {
          content: "";
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid #0095ff;
        }
      }
    }
  }
}

// tooltip 淡入动画
@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}
</style>
